/**
 * Schedule service — calendar-commitment vocabulary (§3 of the scheduling
 * constitution) plus the block mutations the flagship calendar performs.
 *
 * Three jobs:
 *
 *   1. READ the commitment a block already carries. The backend resolves
 *      it (`scheduling_policy.resolved_commitment`) and hands it over
 *      verbatim; the FE never derives Flexible/Confirmed for itself, and
 *      in particular never infers it from `commitment_source` — a source
 *      without a state is still an unknown state. A payload that says
 *      nothing leaves `state: null`, which renders as no chip rather than
 *      as "Confirmed" (SC-35: "unconfirmed = unimportant" is a forbidden
 *      assumption, and so is its mirror — treating silence as commitment).
 *
 *   2. WRITE placement edits, carrying the SC-7 secondary. A user drag or
 *      edit confirms the block by default (`USER_DIRECT`); `keep_flexible`
 *      is the explicit opt-out that moves the block and leaves it
 *      rebalance-eligible. Only ever sent when the user asked for it.
 *
 *   3. READ one day's plan-change history (SC-13), for the pull-only
 *      "plan adjusted" surface. Read-only and on demand: this module has
 *      no push, no polling and no subscription, because SC-13 is quiet by
 *      default and proactive copy about changes belongs to the delivery
 *      gate, not here.
 *
 * Endpoints that speak this vocabulary today: `GET /api/schedule/events/
 * <id>` (`why.commitment_state`), `GET /api/orchestrator/today-view`
 * (`scheduled_events[].commitment`), `GET /api/schedule/changes`
 * (`previous_item.commitment_state`), and `GET /api/schedule/entries`
 * (`commitment_state`, resolved per row by
 * `virtual_recurrence_service._schedule_to_dict`). `readCommitment`
 * accepts both key spellings, so one reader serves all four.
 */

// ───────── vocabulary (constitution §3 — machine symbols, not copy) ─────────

export type CommitmentState = 'flexible' | 'confirmed'

export type CommitmentSource =
  | 'ai_proposal'
  | 'user_direct'
  | 'user_confirmation'
  | 'user_recurring_rule'
  | 'admitted_habit_rule'

export interface BlockCommitment {
  /** null = the payload didn't say. Show nothing; never guess. */
  state: CommitmentState | null
  /** Provenance only. Never render it (SC-36) and never derive state from it. */
  source: CommitmentSource | null
}

export const UNKNOWN_COMMITMENT: BlockCommitment = { state: null, source: null }

const COMMITMENT_STATES: readonly string[] = ['flexible', 'confirmed']

const COMMITMENT_SOURCES: readonly string[] = [
  'ai_proposal',
  'user_direct',
  'user_confirmation',
  'user_recurring_rule',
  'admitted_habit_rule',
]

/**
 * Pull the commitment out of any schedule payload row.
 *
 * `commitment` is today-view's spelling; `commitment_state` is the stored
 * row's, the `why` object's, and the SC-13 change row's. Same §2 dimension-4
 * value — an unrecognised string is treated as absent rather than passed
 * through, so a future backend value can never leak into user-facing copy.
 */
export function readCommitment(row: unknown): BlockCommitment {
  if (!row || typeof row !== 'object') return UNKNOWN_COMMITMENT
  const r = row as Record<string, unknown>
  const rawState = r.commitment ?? r.commitment_state
  const state =
    typeof rawState === 'string' && COMMITMENT_STATES.includes(rawState)
      ? (rawState as CommitmentState)
      : null
  const rawSource = r.commitment_source
  const source =
    typeof rawSource === 'string' && COMMITMENT_SOURCES.includes(rawSource)
      ? (rawSource as CommitmentSource)
      : null
  return { state, source }
}

// ───────── requests ─────────

/**
 * Outcome of a schedule mutation.
 *
 * `pending` is the honest reading of the 202 `applied: false` contract:
 * the server accepted the change but has not applied it. Callers must not
 * claim success in that case — the block has not moved yet.
 */
export interface MutationOutcome<T = Record<string, unknown>> {
  applied: boolean
  pending: boolean
  body: T | null
}

async function requestJson(
  url: string,
  method: 'POST' | 'PUT',
  payload: Record<string, unknown>,
): Promise<MutationOutcome> {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
  if (!res.ok || body?.success === false) {
    const err = typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`
    throw new Error(err)
  }
  // 202 means "accepted, not yet applied" unless the body says otherwise.
  const applied = res.status === 202 ? body?.applied === true : true
  return { applied, pending: !applied, body }
}

/** Fields a placement edit can carry. Times are the wire shapes. */
export interface PlacementEdit {
  title?: string
  description?: string
  /** YYYY-MM-DD */
  date?: string
  /** HH:MM-HH:MM */
  timeSlot?: string
  durationMin?: number
  /**
   * SC-7 secondary. `true` moves the block without confirming it — the
   * user's explicit "this is still Horton's to move". Omitted entirely
   * otherwise: absence is the ratified confirm-on-drag default.
   */
  keepFlexible?: boolean
}

function placementBody(edit: PlacementEdit): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (edit.title !== undefined) body.title = edit.title
  if (edit.description !== undefined) body.description = edit.description
  if (edit.date !== undefined) body.date = edit.date
  if (edit.timeSlot !== undefined) body.time_slot = edit.timeSlot
  if (edit.durationMin !== undefined) body.duration = edit.durationMin
  if (edit.keepFlexible) body.keep_flexible = true
  return body
}

/** PUT a single block (or a recurring master, for "all occurrences"). */
export function updateScheduleEvent(
  eventId: number,
  edit: PlacementEdit,
): Promise<MutationOutcome> {
  return requestJson(`/api/schedule/events/${eventId}`, 'PUT', placementBody(edit))
}

/**
 * Re-place ONE occurrence of a recurring block by writing an exception on
 * its master. `originalDate` is the date the series generates the
 * occurrence on, not the date it is being moved to.
 */
export function createOccurrenceException(
  masterId: number,
  originalDate: string,
  edit: PlacementEdit,
): Promise<MutationOutcome> {
  return requestJson(`/api/schedule/events/${masterId}/exception`, 'POST', {
    ...placementBody(edit),
    original_date: originalDate,
    exception_type: 'modified',
    status: 'scheduled',
  })
}

/**
 * "All occurrences" of a habit lives on the habit definition, not on the
 * schedule row — no placement/commitment semantics apply (habit
 * occurrences are CONFIRMED-via-rule per §3), so this takes no
 * `keep_flexible`.
 */
export function updateHabitDefinition(
  habitId: number | string,
  edit: { title?: string; description?: string; startTime?: string; durationMin?: number },
): Promise<MutationOutcome> {
  const body: Record<string, unknown> = {}
  if (edit.title !== undefined) body.title = edit.title
  if (edit.description !== undefined) body.description = edit.description
  if (edit.startTime !== undefined) body.preferred_start_time = edit.startTime
  if (edit.durationMin !== undefined) body.duration_minutes = edit.durationMin
  return requestJson(`/api/hierarchy/habits/${habitId}`, 'PUT', body)
}

/**
 * Authoritative commitment for ONE block, from the single-block read model
 * (`why`). Single-block only — a list view must read the commitment its
 * own payload carries rather than fanning out one request per row.
 *
 * Never throws: an unknown commitment is a normal answer here (virtual
 * rows, deleted rows, a 404 on someone else's id), and the caller's job is
 * simply to render no chip.
 */
export async function fetchBlockCommitment(eventId: number): Promise<BlockCommitment> {
  try {
    const res = await fetch(`/api/schedule/events/${eventId}`, { credentials: 'include' })
    if (!res.ok) return UNKNOWN_COMMITMENT
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
    if (!body || body.success === false) return UNKNOWN_COMMITMENT
    return readCommitment(body.why)
  } catch {
    return UNKNOWN_COMMITMENT
  }
}

// ───────── SC-13: what changed in one day's plan, and why ─────────

/**
 * One side of a change — previous or replacement.
 *
 * The endpoint's row carries the full `block_facts` projection, which
 * includes internal model words (`block_status`, `commitment_source`, the
 * `schedule_block:42` refs). Those are dropped HERE, at the seam, rather
 * than left for the render layer to remember not to print: SC-36 holds by
 * construction if the internal fields never enter the component's types.
 */
export interface PlanChangeItem {
  /** The user's own words for the item, when the row carried them. */
  title: string | null
  /** YYYY-MM-DD — the day the item itself sits on, which is not always
   *  the day whose plan changed (a move can land on another date). */
  date: string | null
  /** "HH:MM-HH:MM", exactly as the block stores it. */
  timeSlot: string | null
  durationMinutes: number | null
  commitment: CommitmentState | null
}

/**
 * One recorded plan change (`GET /api/schedule/changes`).
 *
 * `reasonCode` and `triggerEvent` stay MACHINE SYMBOLS across this seam.
 * SC-3 splits the work: the server decides what happened, the surface
 * looks the symbol up in a fixed phrase table. Neither side composes a
 * sentence out of the other's fields, so user-facing copy cannot drift
 * from the record.
 */
export interface PlanChange {
  /** Stable per row, for React keys only. */
  key: string
  triggerEvent: string | null
  reasonCode: string | null
  previous: PlanChangeItem | null
  replacement: PlanChangeItem | null
  /** `unscheduled` / `parked` sentinel, or a TEXT ref to where it landed. */
  displacedTo: string | null
}

function readPlanChangeItem(raw: unknown): PlanChangeItem | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const title = typeof r.title === 'string' && r.title.trim() ? r.title.trim() : null
  const date = typeof r.date === 'string' && r.date.trim() ? r.date.trim() : null
  const timeSlot =
    typeof r.time_slot === 'string' && r.time_slot.trim() ? r.time_slot.trim() : null
  const durationMinutes =
    typeof r.duration_minutes === 'number' && Number.isFinite(r.duration_minutes)
      ? r.duration_minutes
      : null
  // Nothing renderable in the payload is the same as no item: an entry with
  // only internal refs left would draw an empty row that says a change
  // happened to something unnameable.
  if (!title && !date && !timeSlot && durationMinutes === null) return null
  return { title, date, timeSlot, durationMinutes, commitment: readCommitment(r).state }
}

function readPlanChange(raw: unknown, index: number): PlanChange | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const previous = readPlanChangeItem(r.previous_item)
  const replacement = readPlanChangeItem(r.replacement_item)
  if (!previous && !replacement) return null
  const displacedRaw = r.displaced_to
  return {
    key: r.id !== undefined && r.id !== null ? `change-${String(r.id)}` : `change-i${index}`,
    triggerEvent: typeof r.trigger_event === 'string' ? r.trigger_event : null,
    reasonCode: typeof r.reason_code === 'string' ? r.reason_code : null,
    previous,
    replacement,
    displacedTo:
      typeof displacedRaw === 'string' && displacedRaw.trim() ? displacedRaw.trim() : null,
  }
}

/**
 * The changes recorded against ONE local day's plan, oldest first.
 *
 * `localDate` is required by the endpoint and supplied by the client: the
 * server refuses to guess which day is "today" (SC-35), and the browser is
 * the side that knows.
 *
 * Never throws, and an unreachable endpoint answers the same as a quiet
 * day — an empty list. The surface renders nothing at all for both, which
 * is the honest reading: we cannot claim a plan changed, and SC-13's
 * promise is only that the record is *available*, never that the user is
 * told something is there.
 */
export async function fetchPlanChanges(localDate: string): Promise<PlanChange[]> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) return []
  try {
    const res = await fetch(
      `/api/schedule/changes?date=${encodeURIComponent(localDate)}`,
      { credentials: 'include' },
    )
    if (!res.ok) return []
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
    if (!body || body.success === false || !Array.isArray(body.changes)) return []
    return body.changes
      .map((row, i) => readPlanChange(row, i))
      .filter((c): c is PlanChange => c !== null)
  } catch {
    return []
  }
}
